import { Users, User, Lock, Sparkles } from "lucide-react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getCurrentUser } from "@/lib/authUser";
import { requestWithAuthRecovery } from "@/lib/authRequest";

export function WorkspaceToggle() {
  const { workspace, selectedGroupId, setSelectedGroupId } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const { canAccessGroups, loading } = useUserRole();

  if (loading) {
    return null;
  }

  const handleWorkspaceChange = async (newWorkspace: "personal" | "groups") => {
    if (!canAccessGroups && newWorkspace === "groups") {
      toast("Groups unavailable", {
        description: "Upgrade to the Business tier to unlock group workspaces.",
      });
      return;
    }

    const currentPath = location.pathname.split('/').pop() || 'hot';
    const pathPrefix = '/dashboard';
    
    if (newWorkspace === "groups") {
      if (!selectedGroupId) {
        const user = await getCurrentUser();
        if (!user) return;

        const { data, error } = await requestWithAuthRecovery(() =>
          supabase
            .from("groups")
            .select("id, group_members!inner(user_id)")
            .eq("group_members.user_id", user.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
        );

        if (error) {
          toast.error("Workspace unavailable", { description: "Please try again in a moment." });
          return;
        }
        
        if (data) {
          setSelectedGroupId(data.id);
          navigate(`${pathPrefix}/groups/${data.id}/${currentPath}`);
        } else {
          setSelectedGroupId(null);
          navigate(`${pathPrefix}/groups`);
        }
      } else {
        navigate(`${pathPrefix}/groups/${selectedGroupId}/${currentPath}`);
      }
    } else {
      // The route is the workspace source of truth. Navigating directly avoids
      // briefly running the still-mounted group page with personal query rules.
      navigate(`${pathPrefix}/personal/${currentPath}`);
    }
  };

  const groupsLocked = !canAccessGroups;

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={workspace === "personal" ? "default" : "ghost"}
              size="sm"
              onClick={() => handleWorkspaceChange("personal")}
              className="gap-2"
            >
              <User className="h-4 w-4" />
              Personal
            </Button>
          </TooltipTrigger>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={workspace === "groups" ? "default" : "ghost"}
              size="sm"
              onClick={() => handleWorkspaceChange("groups")}
              className="gap-2"
              disabled={groupsLocked}
            >
              <Users className="h-4 w-4" />
              Groups
              {groupsLocked && <Lock className="h-3 w-3 ml-1" />}
            </Button>
          </TooltipTrigger>
          {groupsLocked && (
            <TooltipContent>
              <p className="text-xs font-medium">Requires Business tier</p>
              <Button
                size="sm"
                variant="link"
                className="h-auto p-0 text-xs"
                onClick={() => navigate("/pricing")}
              >
                Upgrade →
              </Button>
            </TooltipContent>
          )}
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}